import React from 'react';
import { Link } from 'react-router-dom';
import { Film, Award, Users, Globe, CheckCircle, Heart, Target, Sparkles, Calendar, Building, ArrowRight } from 'lucide-react';
import './AboutPage.css';

const AboutPage = () => {
  const timeline = [
    {
      year: '2016',
      title: 'The Beginning',
      description: 'RSE officially began its journey with our first office — one table, one chair, and endless dedication. Powered by a clear dream: to become a film editor and tell stories that feel real, emotional, and timeless.'
    },
    {
      year: '2018',
      title: 'First Government Project',
      description: 'Secured our first government project, marking the beginning of collaborations with various government bodies and private institutions. Expanded into educational and training films across Gandhinagar.'
    },
    {
      year: '2020',
      title: 'Prestigious Collaborations',
      description: 'Honored with opportunities to collaborate with reputed organizations such as IIPHG and UNICEF, contributing to meaningful projects requiring sensitivity, accuracy, and high production standards.'
    },
    {
      year: '2025',
      title: 'Global Expansion',
      description: 'Expanded our creative network beyond regional boundaries and stepped confidently into the global market. Now proudly providing remote post-production services worldwide.'
    }
  ];

  const philosophy = [
    { title: 'Strong Story Structure', icon: Film },
    { title: 'Emotional Flow', icon: Heart },
    { title: 'Natural Rhythm & Pacing', icon: Sparkles },
    { title: 'Clean, Cinematic Detailing', icon: Target }
  ];

  const whyTrust = [
    'Film-editor mindset, not factory editing',
    'Professionally trained leadership',
    'Experience with government & institutional standards',
    'Seamless remote collaboration',
    'Emotion-driven storytelling',
    'Long-term partnership approach'
  ];

  const tools = ['Adobe Premiere Pro', 'Final Cut Pro', 'DaVinci Resolve'];

  const workflow = [
    'Dedicated editor per project',
    'Structured feedback & revision rounds',
    'Transparent communication',
    'Reliable delivery timelines'
  ];

  return (
    <div className="about-page">
      {/* Hero Section */}
      <section className="about-hero">
        <div className="container">
          <h1 className="about-hero-title">About RSE</h1>
          <p className="about-hero-tagline">We don't just edit videos. We craft films.</p>
          <p className="about-hero-subtitle">Unleashing creativity, one frame at a time.</p>
        </div>
      </section>

      {/* Introduction */}
      <section className="about-intro section">
        <div className="container">
          <div className="intro-content">
            <p className="intro-text large">
              RSE (Red Smile Entertainments) is a global post-production house built on a filmmaker's mindset and a storyteller's heart.
            </p>
            <p className="intro-text">
              We specialize in cinematic video editing for weddings, films, educational content, and institutional projects, serving filmmakers, studios, and organizations worldwide.
            </p>
            <p className="intro-highlight">
              At RSE, editing is not just a service — it is a creative responsibility.
            </p>
          </div>
        </div>
      </section>

      {/* Our Journey Timeline */}
      <section className="journey section">
        <div className="container">
          <h2 className="section-title text-center">
            Our Journey
          </h2>
          <p className="section-subtitle text-center">
            From a small office to a global post-production house
          </p>
          
          <div className="timeline-container">
            {timeline.map((item, index) => (
              <div key={index} className="timeline-item">
                <div className="timeline-marker"></div>
                <div className="timeline-content">
                  <span className="timeline-year">{item.year}</span>
                  <h3 className="timeline-title">{item.title}</h3>
                  <p className="timeline-description">{item.description}</p>
                </div>
              </div>
            ))}
          </div>

          <p className="journey-note">
            Throughout this journey, the constant support and trust of numerous photographers and filmmakers from Gujarat and across India played a vital role in our growth. We move forward with deep gratitude for their belief in us.
          </p>
        </div>
      </section>

      {/* Philosophy */}
      <section className="philosophy section">
        <div className="container">
          <h2 className="section-title text-center">
            Our Philosophy
          </h2>
          <p className="section-subtitle text-center">
            Editing Like a Film
          </p>

          <div className="philosophy-intro">
            <p>At RSE, we believe every story deserves to be treated like a film. Whether it's a wedding, documentary, educational film, or institutional project, our approach remains the same:</p>
          </div>

          <div className="philosophy-grid">
            {philosophy.map((item, index) => {
              const Icon = item.icon;
              return (
                <div key={index} className="philosophy-card">
                  <div className="philosophy-icon">
                    <Icon size={28} />
                  </div>
                  <h3>{item.title}</h3>
                </div>
              );
            })}
          </div>

          <div className="philosophy-quote">
            <p>We don't rush edits. We understand the story before shaping it.</p>
          </div>
        </div>
      </section>

      {/* Founder Message - Quote Style */}
      <section className="founder-message section">
        <div className="container">
          <h2 className="section-title text-center">
            Message from the Founder
          </h2>
          <div className="founder-quote-box">
            <div className="quote-marks">"</div>
            <div className="founder-quote-content">
              <p>I didn't start RSE to just edit videos. I started it to tell stories.</p>
              <p>I am professionally trained in video editing and filmmaking, and my goal has always been to give every project the care it deserves — as if it were my own film.</p>
              <p>Distance doesn't matter in storytelling. Understanding does.</p>
              <p className="quote-highlight">RSE is not just my company — it's my journey as a film editor.</p>
            </div>
            <div className="founder-quote-signature">
              <div className="signature-line"></div>
              <strong>Keyur Rao</strong>
              <span>Founder & Film Editor</span>
            </div>
          </div>
        </div>
      </section>

      {/* Leadership */}
      <section className="leadership section">
        <div className="container">
          <h2 className="section-title text-center">
            Meet the Leadership
          </h2>

          <div className="leadership-grid">
            <div className="leader-card">
              <div className="leader-image-wrapper">
                <img src="/keyur-rao.png" alt="Keyur Rao" className="leader-image" />
              </div>
              <div className="leader-info">
                <h3>Keyur Rao</h3>
                <span className="leader-role">Founder & Creative Director</span>
                <p>A professionally trained film editor with over 8 years of experience in cinematic storytelling, wedding films, and post-production workflows.</p>
              </div>
            </div>

            <div className="leader-card">
              <div className="leader-image-wrapper">
                <img src="/nirali.png" alt="Nirali" className="leader-image" />
              </div>
              <div className="leader-info">
                <h3>Nirali</h3>
                <span className="leader-role">Co-Founder & Senior Editor</span>
                <p>A professionally trained video editor with a strong foundation in cinematic storytelling and technical precision. Her narrative sense, creative discipline, and attention to detail play a key role in maintaining RSE's quality, consistency, and emotional depth across projects.</p>
              </div>
            </div>
          </div>

          <p className="leadership-note text-center">
            Together, the leadership ensures a balance of creative vision and structured execution.
          </p>
        </div>
      </section>

      {/* Our Team */}
      <section className="team section">
        <div className="container">
          <h2 className="section-title text-center">
            Our Team
          </h2>

          <div className="team-content">
            <div className="team-info">
              <div className="team-stat">
                <Users size={48} />
                <span className="team-number">8</span>
                <span className="team-label">Skilled Professional Editors</span>
              </div>
              <p className="team-description">
                RSE is supported by a team of skilled professional editors, each experienced in weddings, films, reels, documentaries, corporate videos, and institutional content.
              </p>
            </div>

            <div className="team-tools">
              <h3>Industry-Standard Tools</h3>
              <div className="tools-list">
                {tools.map((tool, index) => (
                  <div key={index} className="tool-item">
                    <CheckCircle size={18} />
                    <span>{tool}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="team-workflow">
              <h3>Our Workflow</h3>
              <div className="workflow-list">
                {workflow.map((item, index) => (
                  <div key={index} className="workflow-item">
                    <span className="workflow-number">{index + 1}</span>
                    <span>{item}</span>
                  </div>
                ))}
              </div>
              <p className="workflow-note">This allows smooth collaboration with clients across the globe.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Trust RSE */}
      <section className="why-trust section">
        <div className="container">
          <h2 className="section-title text-center">
            Why Clients Trust RSE
          </h2>

          <div className="trust-grid">
            {whyTrust.map((item, index) => (
              <div key={index} className="trust-item">
                <CheckCircle size={24} />
                <span>{item}</span>
              </div>
            ))}
          </div>

          <div className="trust-quote">
            <p>We don't aim to be the cheapest. We aim to be <strong>reliable, cinematic, and trusted.</strong></p>
          </div>
        </div>
      </section>

      {/* Vision */}
      <section className="vision section">
        <div className="container">
          <div className="vision-content">
            <Globe size={48} />
            <h2>Our Vision</h2>
            <p>
              To grow RSE as a globally trusted film-editing house, collaborating with filmmakers, studios, and organizations who believe that editing is an art — not just a process.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="about-cta section">
        <div className="container">
          <div className="cta-content">
            <h2>Let's Turn Your Footage Into a Film</h2>
            <p>If you believe in storytelling, we're already aligned.</p>
            <Link to="/contact" className="btn btn-primary btn-large">
              Contact Us
              <ArrowRight size={20} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
